import React, { useContext, useEffect, useMemo, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactApexChart from 'react-apexcharts';
import { AvgTime } from '../../../../service/UserDashboard';
import AppContext from '../../../../context/AppContext';
import translations from '../../../common/Translations';
import ChartModal from './ChartModal';
import { IoIosTimer } from 'react-icons/io';
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from 'react-icons/md';
import CommonButton from '../../../common/CommonButton';
import ActionIconButton from '../../../common/ActionIconButton';
import ModalCloseButton from '../../../common/ModalCloseButton';
import datePickerPopper from '../../../common/DatePickerPopper';
import { BarChart3, CalendarDays, Eye } from 'lucide-react';

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AvgTimeChart = ({ selectedOrgId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clockData, setClockData] = useState([]);
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  if (oneMonthAgo.getMonth() !== (today.getMonth() - 1 + 12) % 12) {
    oneMonthAgo.setDate(0);
  }
  const [startDate, setStartDate] = useState(oneMonthAgo.toISOString().substring(0, 10));
  const [endDate, setEndDate] = useState(today.toISOString().substring(0, 10));
  const [showDateModal, setShowDateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [chartView, setChartView] = useState("area");
  const { theme, langMode } = useContext(AppContext);
  const isRtl = langMode === "ar";
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;
  const defaultColors = ["#673ab7", "#00897b", "#ffb300"];
  const darkThemeColors = ["#9c27b0", "#26a69a", "#ffca28"];
  const highContrastColors = ["#7b1fa2", "#00695c", "#ff8f00"];

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const parseTime = (timeString) => {
    if (!timeString) return 0;
    const parts = timeString.split(':');
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    return hours * 60 + minutes;
  };

  const handleAvgTime = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AvgTime(selectedOrgId, startDate, endDate);
      if (response?.body?.value) {
        setClockData(response.body.value);
        setCurrentPage(0); // Reset to the first page (index 0)
      } else {
        setClockData([]);
        console.warn("API response missing expected values.");
      }
    } catch (err) {
      setError(err.message);
      setClockData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      handleAvgTime();
    }
  }, [selectedOrgId, startDate, endDate]);

  const startIndex = currentPage * itemsPerPage; // CORRECTED logic
  const endIndex = startIndex + itemsPerPage;
  const safeClockData = Array.isArray(clockData) ? clockData : [];
  const paginatedData = safeClockData.slice(startIndex, endIndex);

  const seriesData = [
    {
      name: translations[langMode].totalWorked || 'Total Worked',
      data: (Array.isArray(paginatedData) ? paginatedData : []).map(item => parseTime(item?.totalWorkedFormatted))
    },
    {
      name: translations[langMode].averageDaily || 'Average Daily',
      data: (Array.isArray(paginatedData) ? paginatedData : []).map(item => parseTime(item?.averageDailyFormatted))
    }
  ];

  const categories = (Array.isArray(paginatedData) ? paginatedData : []).map(item => item?.userName || "");
  const isDataEmpty = safeClockData.length === 0;
  const totalEntries = (Array.isArray(paginatedData) ? paginatedData : []).length;
  const totalSeriesValues = seriesData.reduce((acc, curr) => acc + (curr?.data || []).reduce((a, b) => a + b, 0), 0);
  const totalPages = Math.ceil(safeClockData.length / itemsPerPage);
  const normalizedSeriesData = seriesData.map(series => (series?.data || []).map(val => totalSeriesValues > 0 ? (val / totalSeriesValues) * 100 : 0));

  const baseChartOptions = {
    chart: { toolbar: { show: false }, animations: { enabled: true }, foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333" },
    dataLabels: { enabled: true, style: { fontSize: "12px", colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#fff")] } },
    legend: { show: true, position: "bottom", labels: { colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333" } },
    tooltip: { enabled: true, theme: theme === "dark" ? "dark" : "light", y: { formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` } },
    colors: getChartColors(),
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: { parentHeightOffset: 0 },
          dataLabels: { enabled: false },
          legend: {
            fontSize: "10px",
            itemMargin: { horizontal: 5, vertical: 2 },
          },
          xaxis: {
            labels: {
              rotate: 0,
              rotateAlways: false,
              hideOverlappingLabels: true,
              trim: true,
              style: { fontSize: "9px" },
              formatter: (value) => {
                const text = String(value ?? "");
                return text.length > 9 ? `${text.slice(0, 8)}…` : text;
              },
            },
          },
          yaxis: {
            tickAmount: 4,
            labels: {
              minWidth: 34,
              maxWidth: 42,
              style: { fontSize: "9px" },
            },
          },
          grid: {
            padding: { left: 2, right: 5, bottom: 4 },
          },
        },
      },
    ],
  };

  const BarChartView = ({ series, categories }) => {
    const barOptions = {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: "bar", background: theme === "dark" ? "#1F2937" : "#FFFFFF" },
      xaxis: { categories, labels: { style: { colors: baseChartOptions.chart.foreColor } } },
      yaxis: { labels: { style: { colors: baseChartOptions.chart.foreColor, fontSize: '13px', fontWeight: 500 }, formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` }, axisBorder: { show: false }, axisTicks: { show: false }, title: { text: translations[langMode].numberOfUsers || "Number of task", style: { color: baseChartOptions.chart.foreColor, fontSize: '14px', fontWeight: 600 } } },
      plotOptions: { bar: { horizontal: false, borderRadius: 8, columnWidth: '45%' } },
      dataLabels: { enabled: true, offsetY: -20, style: { fontSize: '13px', colors: [theme === "dark" ? "#FFF" : "#333"], fontWeight: 600 }, background: { enabled: true, foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF", padding: 6, borderRadius: 4, borderWidth: 1, borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0", opacity: 0.9, dropShadow: { enabled: true, top: 1, left: 1, blur: 2, opacity: 0.2 } }, formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` },
      grid: { show: true, borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0", strokeDashArray: 4, position: 'back', xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
      tooltip: { y: { formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` } },
      legend: { show: true },
      colors: getChartColors()
    };
    return <ReactApexChart options={barOptions} series={series} type="bar" width="100%" height="100%" />;
  };

  const AreaChartView = ({ series, categories }) => {
    const areaOptions = {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: "area" },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
          formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m`
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        }, enabled: true, formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m`
      },
      tooltip: {
        y: { formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back'
      },
      colors: getChartColors()
    };
    return <ReactApexChart options={areaOptions} series={series} type="area" width="100%" height="100%" />;
  };

  const RadialBarChartView = ({ series, labels }) => {
    const radialBarOptions = {
      ...baseChartOptions, chart: { ...baseChartOptions.chart, type: "radialBar" }, plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%', // Adjust size to create concentric effect
            background: 'transparent',
            image: undefined,
          },
          track: {
            show: true,
            startAngle: undefined,
            endAngle: undefined,
            background: theme === "dark" ? "#374151" : "#e0e0e0", // Track color for un-filled part
            strokeWidth: '97%', // Make track thinner to create gap
            opacity: 1,
            margin: 10, // Margin between each ring
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: 4,
              opacity: 0.15
            }
          },
          dataLabels: {
            value: {
              show: true,
              fontSize: '18px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontWeight: 700,
              color: baseChartOptions.chart.foreColor,
              offsetY: 5,
              formatter: (val) => `${Number.isFinite(val) ? val.toFixed(0) : 0}%`
            },
            total: { show: true, label: translations[langMode].total || "Total", formatter: () => totalEntries }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: getChartColors(), // Ensure gradients use the defined colors
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      labels: labels, // Labels for each series (outer ring)
      stroke: {
        lineCap: 'round' // Rounded ends for the bars
      },
      legend: {
        show: true, // Keep legend for clarity
        position: 'bottom',
        offsetY: 0,
        height: 50,
        markers: {
          fillColors: getChartColors()
        }
      },
      labels: labels, colors: getChartColors()
    };
    const donutSeries = (Array.isArray(paginatedData) ? paginatedData : []).map(item => {
      const totalMinutes = parseTime(item?.totalWorkedFormatted) + parseTime(item?.averageDailyFormatted);
      return totalSeriesValues > 0 ? (totalMinutes / totalSeriesValues) * 100 : 0;
    });
    return <ReactApexChart options={radialBarOptions} series={donutSeries} type="radialBar" width="100%" height="100%" />;
  };

  const DonutChartView = ({ series, labels }) => {
    const donutOptions = { ...baseChartOptions, labels: labels, dataLabels: { ...baseChartOptions.dataLabels, formatter: (val, opts) => totalSeriesValues > 0 ? `${(val / totalSeriesValues * 100).toFixed(1)}%` : '' }, plotOptions: { pie: { donut: { size: '65%', labels: { total: { label: translations[langMode].total || "Total", formatter: () => totalEntries } } } } }, stroke: { colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")] }, tooltip: { y: { formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` } }, colors: getChartColors() };
    const donutSeries = (Array.isArray(paginatedData) ? paginatedData : []).map(item => parseTime(item?.totalWorkedFormatted) + parseTime(item?.averageDailyFormatted));
    return <ReactApexChart options={donutOptions} series={donutSeries} type="donut" width="100%" height="100%" />;
  };

  const RadarChartView = ({ series, categories }) => {
    const radarOptions = {
      ...baseChartOptions,
      chart: { ...baseChartOptions.chart, type: "radar" },
      xaxis: { categories: categories, labels: { style: { colors: baseChartOptions.chart.foreColor } } },
      yaxis: { show: false },
      dataLabels: {
        style: {
          fontSize: '12px',
          colors: [baseChartOptions.chart.foreColor]
        },
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
        enabled: true, formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m`
      },
      tooltip: {
        y: { formatter: (val) => `${Math.floor(val / 60)}h ${val % 60}m` },
      },
      legend: { show: true },
      stroke: {
        show: true,
        width: 2,
        curve: 'smooth',
        lineCap: 'round',
      },
      fill: {
        opacity: 0.4,
      },
      markers: {
        size: 5,
        hover: {
          size: 7,
        },
        colors: getChartColors(),
        strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
        strokeWidth: 2,
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          left: 20,
          right: 20
        }
      },
      colors: getChartColors()
    };
    return <ReactApexChart options={radarOptions} series={series} type="radar" width="100%" height="100%" />;
  };

  return (
    <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"} p-2`}>
      <div className="flex w-full items-center justify-between gap-2 py-1">
        <h2 className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-semibold sm:gap-2 sm:text-base">
          <IoIosTimer size={18} className="shrink-0 text-purple-500 sm:h-5 sm:w-5" />
          <label className="mt-0.5 truncate">{translations[langMode].fieldagentAvgwork}</label>
        </h2>

        <div className="flex shrink-0 items-center gap-1">
          <ActionIconButton
            type="button"
            tone="gray"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowDateModal(true)}
            aria-label={translations[langMode].RangeFilter}
            tooltipText={translations[langMode].RangeFilter}
            tooltipPosition="bottom"
          >
            <CalendarDays size={18} />
          </ActionIconButton>
          <ActionIconButton
            type="button"
            tone="gray"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowModal(true)}
            aria-label={translations[langMode]?.ViewChart}
            tooltipText={translations[langMode]?.ViewChart}
            tooltipPosition="bottom"
          >
            <Eye size={18} />
          </ActionIconButton>
        </div>
      </div>

      {isDataEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                ? "bg-[#236DB4]/20 text-[#236DB4]"
                : theme === "high-contrast"
                  ? "bg-black text-yellow-300"
                  : "bg-[#236DB4]/10 text-[#236DB4]"
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
              {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="h-full min-h-[220px] w-full flex-1 sm:min-h-[300px]">
            {chartView === "chart" ? <RadialBarChartView series={normalizedSeriesData} labels={categories} /> :
              chartView === "donut" ? <DonutChartView series={seriesData} labels={categories} /> :
                chartView === "area" ? <AreaChartView series={seriesData} categories={categories} /> :
                  chartView === "radar" ? <RadarChartView series={seriesData} categories={categories} /> :
                    <BarChartView series={seriesData} categories={categories} />}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-between items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
                className={`p-2 rounded-full transition-colors duration-200 ${currentPage === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                aria-label="Previous page"
              >
                {isRtl ? (
                  <MdKeyboardDoubleArrowRight size={20} />
                ) : (
                  <MdKeyboardDoubleArrowLeft size={20} />
                )}
              </button>
              <span className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                {translations[langMode].page || "Page"} {currentPage + 1} {translations[langMode].of || "of"} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage === totalPages - 1}
                className={`p-2 rounded-full transition-colors duration-200 ${currentPage === totalPages - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'}`}
                aria-label="Next page"
              >
                {isRtl ? (
                  <MdKeyboardDoubleArrowLeft size={20} />
                ) : (
                  <MdKeyboardDoubleArrowRight size={20} />
                )}
              </button>
            </div>
          )}
        </>
      )}

      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div data-tooltip-bounds className="relative max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="mb-4 pe-10">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{translations[langMode].selectDateRange}</h3>
            </div>
            <ModalCloseButton
              className="absolute end-4 top-4"
              onClick={() => setShowDateModal(false)}
            />
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].startDate}</label>
              <DatePicker
                wrapperClassName="w-full"
                selected={parseYmd(startDate)}
                onChange={(date) => {
                  setStartDate(date ? formatYmd(date) : "");
                  if (date && parseYmd(endDate) && parseYmd(endDate) < date) {
                    setEndDate(formatYmd(date));
                  }
                }}
                placeholderText={translations[langMode].startDate}
                maxDate={todayPicker}
                selectsStart
                startDate={parseYmd(startDate)}
                endDate={parseYmd(endDate)}
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            <div className="min-w-0">
              <label className="text-sm font-medium">{translations[langMode].endDate}</label>
              <DatePicker
                wrapperClassName="w-full"
                selected={parseYmd(endDate)}
                onChange={(date) => setEndDate(date ? formatYmd(date) : "")}
                placeholderText={translations[langMode].endDate}
                minDate={parseYmd(startDate)}
                maxDate={todayPicker}
                selectsEnd
                startDate={parseYmd(startDate)}
                endDate={parseYmd(endDate)}
                disabled={!startDate}
                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                calendarClassName="!rounded-lg shadow-lg"
                popperPlacement="bottom-start"
                popperContainer={datePickerPopper}
              />
            </div>
            </div>
            <div className="flex justify-end gap-3">
              <CommonButton colorIntent="danger"
               onClick={() => setShowDateModal(false)} 
                 tooltipText={translations[langMode].cancelandclose}
                  tooltipPosition="top"
               variant='danger'  >
                {translations[langMode].cancle}
                </CommonButton>
              <CommonButton colorIntent="primary"
              onClick={() => { setShowDateModal(false);
               /* handleAvgTime(); // redundant: date pickers call
                  setStartDate/setEndDate directly, and the effect (deps:
                  [selectedOrgId, startDate, endDate]) already re-fetches on
                  that change. */ }}
               variant='primary'
                tooltipText={translations[langMode].applytheselecteddaterange}
                tooltipPosition="top"
               >
                {translations[langMode].apply}
                </CommonButton>
            </div>
          </div>
        </div>
      )}
      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        currentChartView={chartView}
        availableChartTypes={["radialBar", "donut", "bar", "area", "radar"]}
      />
    </div>
  );
};

export default AvgTimeChart;
